<template>
    <Dialog
        :visible="open"
        modal
        header="Rotate the credential"
        :closable="!busy"
        :dismissable-mask="false"
        :draggable="false"
        class="rotate-credential"
        :style="{ width: '30rem', maxWidth: '92vw' }"
        @update:visible="onVisible"
    >
        <p class="rotate-credential__message">
            A new credential is made and its secret shown once. The current credential keeps working for
            <strong>{{ hours }} {{ hoursWord }}</strong> after the new one is made, then it is revoked. Change the
            hours if the program needs longer to switch; {{ DEFAULT_HOURS }} hours is the default.
        </p>
        <label class="rotate-credential__field">
            <span>Hours the current credential keeps working</span>
            <InputNumber
                v-model="hours"
                :min="1"
                :max="MAX_HOURS"
                :use-grouping="false"
                show-buttons
                :step="1"
                input-id="rotate-credential-hours"
                data-field="hours"
            />
        </label>
        <p v-if="error" class="rotate-credential__error" role="alert">{{ error }}</p>
        <template #footer>
            <Button label="Cancel" severity="secondary" variant="outlined" :disabled="busy" @click="cancel" />
            <Button
                label="Rotate"
                icon="pi pi-refresh"
                :loading="busy"
                :disabled="!isValid"
                class="rotate-credential__confirm"
                data-action="confirm-rotate"
                @click="emit('rotate', hours)"
            />
        </template>
    </Dialog>
</template>

<script setup>
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import InputNumber from 'primevue/inputnumber'
import { computed, ref, watch } from 'vue'

defineOptions({ name: 'RotateCredentialDialog' })

// What a rotation asks before it happens: how long the credential being replaced keeps working.
// The policy (ruled 2026-10-19): the old credential expires 24 hours after the rotation unless
// the administrator says otherwise, in whole hours, at least one. The identity server caps it at
// 720 hours (30 days); this dialog holds the same bounds so a refusal is never a surprise.
const props = defineProps({
    busy: { type: Boolean, default: false },
    // The last attempt's verdict, shown inside the dialog; empty for none.
    error: { type: String, default: '' }
})

const open = defineModel('open', { type: Boolean, default: false })

const emit = defineEmits(['rotate'])

const DEFAULT_HOURS = 24
const MAX_HOURS = 720

const hours = ref(DEFAULT_HOURS)

// Each opening starts from the default: a longer window chosen once is not a new policy.
watch(open, function (isOpen) {
    if (isOpen) {
        hours.value = DEFAULT_HOURS
    }
})

const isValid = computed(() => Number.isInteger(hours.value) && hours.value >= 1 && hours.value <= MAX_HOURS)

const hoursWord = computed(function () {
    if (hours.value === 1) {
        return 'hour'
    }
    return 'hours'
})

function cancel() {
    open.value = false
}

function onVisible(visible) {
    if (visible === false && props.busy === false) {
        open.value = false
    }
}
</script>

<style scoped>
.rotate-credential__message {
    margin: 0 0 14px;
    font-size: 14px;
    line-height: 1.5;
    color: var(--p-text-color);
}

.rotate-credential__field {
    display: flex;
    flex-direction: column;
    gap: 6px;
    font-size: 12px;
    font-weight: 600;
    color: var(--p-text-muted-color);
}

.rotate-credential__error {
    margin: 12px 0 0;
    font-size: 13px;
    color: var(--p-status-danger);
}
</style>
